import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, filter, first, map, startWith } from 'rxjs';
import { ApiEnums } from '@app/api';
import { ContextService } from '@core/providers/context/context.service';
import {
  DialogActionsDirective,
  DialogComponent,
  DialogContentDirective,
  DialogTitleDirective,
} from '@shared/components/dialog/dialog.component';
import { UnsavedChangesService, UnsavedChangesTracker } from '@shared/guards';
import { AskFrequencyKind, askFrequencyLabel } from '../ask-frequency';
import { AskFrequencyFieldComponent } from '../ask-frequency-field/ask-frequency-field.component';
import { askFrequencyFromGroup, createAskFrequencyGroup } from '../ask-frequency-form';
import {
  AskFrequencyOverrideDialogData,
  AskFrequencyOverrideDialogResult,
} from './ask-frequency-override-dialog.interface';

export const askFrequencyOverrideDialogConfig: MatDialogConfig = {
  width: '600px',
  maxWidth: '96vw',
  disableClose: true,
};

type OverrideMode = 'follow' | 'override';

@Component({
  selector: 'app-ask-frequency-override-dialog',
  templateUrl: './ask-frequency-override-dialog.component.html',
  styleUrls: ['./ask-frequency-override-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatRadioModule,
    DialogComponent,
    DialogTitleDirective,
    DialogContentDirective,
    DialogActionsDirective,
    AskFrequencyFieldComponent,
    TranslatePipe,
  ],
})
export class AskFrequencyOverrideDialogComponent {
  protected readonly data = inject<AskFrequencyOverrideDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<
      MatDialogRef<
        AskFrequencyOverrideDialogComponent,
        AskFrequencyOverrideDialogResult | undefined
      >
    >(MatDialogRef);
  private readonly translateService = inject(TranslateService);
  private readonly unsavedChangesService = inject(UnsavedChangesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly contextService = inject(ContextService);

  protected readonly form = new FormGroup({
    mode: new FormControl<OverrideMode>(this.data.override ? 'override' : 'follow', {
      nonNullable: true,
    }),
    frequency: createAskFrequencyGroup({
      whenToAsk: this.data.override ? this.data.override.whenToAsk : this.data.account.whenToAsk,
      countingScope: this.data.account.countingScope,
    }),
  });
  private readonly unsavedChanges = new UnsavedChangesTracker(this.form);

  protected readonly isOverride = signal(this.form.controls.mode.value === 'override');
  protected readonly isSaving = signal(false);
  protected readonly unitParams = this.contextService.getAccountChildrenNameParams();
  protected readonly accountFrequencyText = askFrequencyLabel(
    this.data.account.whenToAsk,
    this.data.account.countingScope,
    this.unitParams,
    (key, params) => String(this.translateService.instant(key, params))
  );
  protected readonly introKey = this.data.introKey ?? 'SHARED.ASK_FREQUENCY.OVERRIDE_DIALOG.INTRO';
  /** An override keeps the account's counting, which is not always per unit, so it is said. */
  protected readonly countingKey =
    this.data.account.countingScope === ApiEnums.CountingScope.Account
      ? 'SHARED.ASK_FREQUENCY.OVERRIDE_DIALOG.COUNTED_PER_ACCOUNT'
      : 'SHARED.ASK_FREQUENCY.OVERRIDE_DIALOG.COUNTED_PER_UNIT';
  /** Every time is not counted, so the counting line only shows for a frequency that is. */
  protected readonly isCounted = toSignal(
    this.form.controls.frequency.controls.kind.valueChanges.pipe(
      startWith(this.form.controls.frequency.controls.kind.value),
      map(kind => kind !== AskFrequencyKind.EVERY_TIME)
    ),
    { requireSync: true }
  );

  constructor() {
    this.syncFrequencyToMode(this.form.controls.mode.value);
    this.form.controls.mode.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mode => this.syncFrequencyToMode(mode));

    // `disableClose` keeps Material from closing on ESC or a backdrop click; both still notify
    // us, so an edit can be confirmed away instead of silently discarded.
    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.requestClose());
    this.dialogRef
      .keydownEvents()
      .pipe(
        filter(event => event.key === 'Escape'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.requestClose());
  }

  protected save(): void {
    if (this.isSaving()) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    this.saveRequest$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => this.dialogRef.close(result),
        // The opener's call shows the error; the dialog stays open with the user's edits.
        error: () => this.isSaving.set(false),
      });
  }

  protected requestClose(): void {
    if (this.isSaving()) {
      return;
    }
    if (!this.unsavedChanges.hasChanges()) {
      this.dialogRef.close();
      return;
    }

    this.unsavedChangesService
      .confirmDiscard$()
      .pipe(first(), filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dialogRef.close());
  }

  private saveRequest$(): Observable<AskFrequencyOverrideDialogResult> {
    if (this.form.controls.mode.value === 'follow') {
      return this.data
        .followAccount()
        .pipe(map((): AskFrequencyOverrideDialogResult => ({ override: null })));
    }

    const { whenToAsk } = askFrequencyFromGroup(this.form.controls.frequency);
    return this.data
      .saveOverride(whenToAsk)
      .pipe(map(saved => ({ override: { whenToAsk: saved } })));
  }

  /** Following the account leaves nothing to fill in, so the frequency must not block saving. */
  private syncFrequencyToMode(mode: OverrideMode): void {
    this.isOverride.set(mode === 'override');
    if (mode === 'override') {
      this.form.controls.frequency.enable({ emitEvent: false });
    } else {
      this.form.controls.frequency.disable({ emitEvent: false });
    }
  }
}
